import { AlertCircle } from "lucide-react";
import { Box, Button, Flex, Grid, Text, Textarea } from "@chakra-ui/react";
import { useNewOrderStore } from "../../../stores/order.store";
import { useSimpleToast } from "../../../hooks/toast.hooks";
import { createFile } from "../../../mutations/file.mutations";
import PageTitle from "../../../components/PageTitle";
import FileInputButton from "../../../components/FileInputButton";
import { EarSectionRight } from "./EarSectionRight";
import { EarSectionLeft } from "./EarSectionLeft";
import { OrdersNewPageBaseLayout } from "./OrdersNewPageBaseLayout";
import { OrdersNewPageBaseLayoutButton } from "./OrdersNewPageBaseLayoutButton";

export const ChooseSetupLayout = () => {
	const {
		setError,
		showError,
		commentary,
		setCommentary,
		setAdditionalFiles,
		additionalFiles,
		canGoNextStep,
		goNextStep,
		hasLeftEar,
		hasRightEar,
		leftEarForm,
		rightEarForm,
	} = useNewOrderStore();
	const toast = useSimpleToast();
	const hasVariantDifference =
		hasLeftEar === true &&
		hasRightEar === true &&
		Object.keys(leftEarForm).some((key) => {
			const leftValue = leftEarForm[key];
			const rightValue = rightEarForm[key];
			if (!leftValue || !rightValue) {
				return false;
			}
			return leftValue.uuid !== rightValue.uuid;
		});

	return (
		<OrdersNewPageBaseLayout>
			<Flex direction="column" gap={12} alignItems="center">
				<Flex direction="column" alignItems="center" gap={6}>
					<PageTitle>Oreille(s) concernée(s)</PageTitle>
				</Flex>

				<Grid
					gridTemplateColumns="1fr 1fr"
					alignItems="flex-start"
					w="min(1600px,90vw)"
					justifyContent="space-between"
					gap={4}
				>
					<EarSectionRight />
					<EarSectionLeft />
				</Grid>

				<Flex direction="column" w="100%" gap={4}>
					<Text lineHeight="21px" fontSize="16px" fontWeight="600">
						Commentaire
					</Text>
					<Textarea
						defaultValue={commentary}
						onChange={(evt) => setCommentary(evt.target.value)}
					/>
				</Flex>

				<Flex direction="column" w="100%">
					<Flex gap={4}>
						<FileInputButton
							name={"additionalFiles"}
							maxNumberOfFiles={Number.POSITIVE_INFINITY}
							onFileChange={async (files: File[] | null) => {
								if (files === null) {
									return;
								}
								try {
									const savedFiles = await Promise.all(
										files?.map((file) =>
											createFile({
												file,
												fileName: undefined,
											}),
										),
									);
									setAdditionalFiles(savedFiles);
								} catch (err) {
									toast.error("Fichier", "Erreur lors de l'ajout de fichier");
								}
							}}
						/>
						{Array.isArray(additionalFiles) && additionalFiles.length > 0 && (
							<Button variant="outline" onClick={() => setAdditionalFiles([])}>
								Supprimer{" "}
							</Button>
						)}
					</Flex>
					<Flex direction="column">
						{additionalFiles?.map((additionalFile) => (
							<Box key={additionalFile.id}>{additionalFile.fileName}</Box>
						))}
					</Flex>
				</Flex>

				{canGoNextStep() === false && showError === true && (
					<div className="w-full bg-error-50 border! border-error-300! rounded-lg flex items-center gap-2 p-2">
						<AlertCircle className="text-error-600 w-3.5 h-3.5" />
						<Text className="text-error-600" fontSize="14px" fontWeight={600}>
							Veuillez remplir tous les champs afin de valider la commande
						</Text>
					</div>
				)}
				{hasVariantDifference && (
					<div className="w-full bg-orange-50 border! border-orange-300! rounded-lg flex items-center gap-2 p-2">
						<AlertCircle className="text-orange-600 w-3.5 h-3.5" />
						<Text color="orange.700" fontSize="14px" fontWeight={600}>
							Pour votre information: certaines options ne sont pas les mêmes
							des deux côtés
						</Text>
					</div>
				)}

				<OrdersNewPageBaseLayoutButton
					allowInvalidSubmit
					onValidate={() => {
						if (canGoNextStep()) {
							goNextStep();
						} else {
							setError();
						}
					}}
				/>
			</Flex>
		</OrdersNewPageBaseLayout>
	);
};
